import React, { useEffect, useMemo, useState } from "react";
import { client } from "@/lib/api";
import { DeskMetrics } from "@/components/admin/DeskMetrics";


/** Wave 10C (Vania 1 Oct) — admin analytics desk. All sections hit the
 *  /api/admin/analytics/* endpoints which are admin-only (require_admin).
 *  Staff + test accounts excluded by default; toggle on top-right flips it. */
const DAY_PRESETS = [
  { key: "7d",  label: "last 7 days",  days: 7 },
  { key: "14d", label: "last 14 days", days: 14 },
  { key: "30d", label: "last 30 days", days: 30 },
  { key: "90d", label: "last 90 days", days: 90 },
];

function daysAgoIso(d) {
  const dt = new Date(Date.now() - d * 86400000);
  return dt.toISOString();
}
function fmtPct(x) {
  if (x == null) return "—";
  const n = Math.round(x * 1000) / 10;
  const sign = n > 0 ? "+" : "";
  return `${sign}${n}%`;
}
function fmtNum(x) {
  if (x == null) return "—";
  return x.toLocaleString();
}

const METRIC_LABEL = {
  unique_visitors: "unique visitors",
  accounts_created: "accounts created",
  sign_ins: "sign-ins",
  signed_in_members: "signed-in members",
  trials_started: "trials started",
  paid_members: "paid members",
  visitor_to_account: "visitor → account",
};

export default function Analytics() {
  const [preset, setPreset] = useState("14d");
  const [includeStaff, setIncludeStaff] = useState(false);
  const [summary, setSummary] = useState(null);
  const [series, setSeries] = useState([]);
  const [refs, setRefs] = useState({ referrers: [], utm_sources: [] });
  const [devices, setDevices] = useState(null);
  const [top, setTop] = useState(null);
  const [cities, setCities] = useState(null);
  const [gender, setGender] = useState(null);
  const [loading, setLoading] = useState(true);

  const range = useMemo(() => {
    const days = (DAY_PRESETS.find((p) => p.key === preset) || DAY_PRESETS[1]).days;
    return { from: daysAgoIso(days), to: new Date().toISOString() };
  }, [preset]);

  // WW-148 (Claude QA round 2 · 13 Oct) · drop the ref gate.  Vania
  // showed the gate broke a fresh load because the first effect run was
  // aborted in cleanup while the ref was already set, so the second run
  // bailed and nothing ever reached setState.  Also noted: this is a
  // production build, so React.StrictMode is NOT what double-fires the
  // effect — the real cause is an auth-provider state transition
  // (`loading→false`, then `user` set) that re-renders the Outlet.
  // Fix: AbortController per run.  The first run's requests get
  // cancelled in cleanup; only the surviving run commits state.
  useEffect(() => {
    const ac = new AbortController();
    setLoading(true);
    setCities(null);  // WW-145 · clear so stale dots never flash during a range change.
    const q = `?from=${encodeURIComponent(range.from)}&to=${encodeURIComponent(range.to)}&include_staff=${includeStaff ? 1 : 0}`;
    const cfg = { signal: ac.signal };
    Promise.all([
      client.get(`/admin/analytics/summary${q}`, cfg).then((r) => r.data).catch(() => null),
      client.get(`/admin/analytics/series${q}`, cfg).then((r) => r.data).catch(() => null),
      client.get(`/admin/analytics/referrers${q}`, cfg).then((r) => r.data).catch(() => null),
      client.get(`/admin/analytics/devices${q}`, cfg).then((r) => r.data).catch(() => null),
      client.get(`/admin/analytics/top${q}`, cfg).then((r) => r.data).catch(() => null),
      client.get(`/admin/analytics/cities${q}&fast=1`, cfg).then((r) => r.data).catch(() => null),
      client.get(`/admin/analytics/gender${q}`, cfg).then((r) => r.data).catch(() => null),
    ]).then(([s, se, re, de, tp, ci, ge]) => {
      if (ac.signal.aborted) return;
      setSummary(s);
      setSeries(se?.series || []);
      setRefs(re || { referrers: [], utm_sources: [] });
      setDevices(de);
      setTop(tp);
      setCities(ci);
      setGender(ge);
      setLoading(false);
      // Hydration pass · fires only when there are still uncached IPs.
      if (ci && (ci.uncached_ip_count || 0) > 0) {
        client.get(`/admin/analytics/cities${q}`, cfg)
          .then((r) => { if (!ac.signal.aborted) setCities(r.data); })
          .catch(() => {});
      }
    });
    return () => { ac.abort(); };
  }, [range.from, range.to, includeStaff]);

  return (
    <div data-testid="admin-analytics" className="p-6">
      <header className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold lowercase">analytics</h1>
          <p className="text-sm text-neutral-500">how the app is being used · staff excluded by default</p>
        </div>
        <div className="flex items-center gap-2">
          {DAY_PRESETS.map((p) => (
            <button key={p.key} data-testid={`range-${p.key}`}
                    onClick={() => setPreset(p.key)}
                    className={`rounded-full border px-3 py-1 text-xs font-semibold ${preset === p.key ? "bg-neutral-900 text-white border-neutral-900" : "bg-white border-neutral-300 hover:bg-neutral-100"}`}>
              {p.label}
            </button>
          ))}
          <label className="ml-3 flex items-center gap-2 text-xs">
            <input type="checkbox" data-testid="include-staff-toggle"
                   checked={includeStaff} onChange={(e) => setIncludeStaff(e.target.checked)} />
            include staff + test accounts
          </label>
        </div>
      </header>

      {loading && <p className="text-sm text-neutral-500">loading…</p>}

      <DeskMetrics />

      {summary && (
        <section data-testid="analytics-summary" className="mb-8 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {Object.entries(summary.metrics).map(([k, v]) => (
            <div key={k} className="rounded-xl border border-neutral-200 bg-white p-4" data-testid={`metric-${k}`}>
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-neutral-500">{METRIC_LABEL[k] || k}</p>
              <p className="mt-1 text-2xl font-bold text-neutral-900">
                {k === "visitor_to_account" ? (v.value != null ? `${(v.value * 100).toFixed(1)}%` : "—") : fmtNum(v.value)}
              </p>
              <p className={`text-xs ${v.change == null ? "text-neutral-400" : v.change >= 0 ? "text-green-700" : "text-red-600"}`}>
                {fmtPct(v.change)} vs prev
              </p>
              <p className="mt-1 text-[11px] text-neutral-400">{summary.definitions[k]}</p>
            </div>
          ))}
          {/* Wave 16c.1 (Vania 3 Oct) · "on trial now" is a snapshot, not a
              window delta. Shows the one trial started 30 Sep even though
              trials_started (event-based) can't see pre-tracking rows. */}
          {summary.snapshots && (
            <div className="rounded-xl border border-neutral-200 bg-white p-4" data-testid="metric-trials-active-now">
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-neutral-500">on trial now</p>
              <p className="mt-1 text-2xl font-bold text-neutral-900">{fmtNum(summary.snapshots.trials_active_now)}</p>
              <p className="text-xs text-neutral-500">
                {summary.snapshots.soonest_trial_end_at
                  ? `soonest ends ${summary.snapshots.soonest_trial_end_at.slice(0, 10)}`
                  : "no trials ending"}
              </p>
              <p className="mt-1 text-[11px] text-neutral-400">snapshot · ignores date range</p>
            </div>
          )}
          {/* Wave 16c.2 · founding members so far / founding cap. */}
          {summary.snapshots && (
            <div className="rounded-xl border border-neutral-200 bg-white p-4" data-testid="metric-founding-members">
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-neutral-500">founding crew</p>
              <p className="mt-1 text-2xl font-bold text-neutral-900">
                {fmtNum(summary.snapshots.founding_members_so_far)}<span className="text-sm text-neutral-500"> / {fmtNum(summary.snapshots.founding_cap)}</span>
              </p>
              <p className="text-xs text-neutral-500">
                {Math.max(0, (summary.snapshots.founding_cap || 0) - (summary.snapshots.founding_members_so_far || 0))} seats left
              </p>
              <p className="mt-1 text-[11px] text-neutral-400">snapshot · ignores date range</p>
            </div>
          )}
        </section>
      )}

      <section className="mb-8">
        <h2 className="mb-2 text-sm font-semibold uppercase tracking-[0.14em] text-neutral-500">daily chart</h2>
        <SparkChart series={series} loading={loading} />
      </section>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Panel title="top events" testId="panel-top-events">
          {top && top.top_events?.length ? (
            <ul className="divide-y divide-neutral-200">
              {top.top_events.map((e) => (
                <li key={e.event_id} className="flex items-center justify-between py-2 text-sm">
                  <a href={`/app/event/${e.event_id}`} target="_blank" rel="noreferrer"
                     className="truncate pr-2 lowercase hover:underline">{e.title || e.event_id}</a>
                  <span className="font-mono text-xs text-neutral-600">{fmtNum(e.n)}</span>
                </li>
              ))}
            </ul>
          ) : <p className="text-sm text-neutral-400">no events yet in this range.</p>}
        </Panel>
        <Panel title="top searches" testId="panel-top-searches">
          {top && top.top_searches?.length ? (
            <ul className="divide-y divide-neutral-200">
              {top.top_searches.map((s) => (
                <li key={s.key} className="flex items-center justify-between py-2 text-sm">
                  <span className="truncate pr-2 lowercase">{s.key}</span>
                  <span className="font-mono text-xs text-neutral-600">{fmtNum(s.n)}</span>
                </li>
              ))}
            </ul>
          ) : <p className="text-sm text-neutral-400">no searches yet in this range.</p>}
          {/* Wave 16b.4 (Vania 2 Oct) · zero-result searches surfaced below
              the top list so we can see what members tried but got nothing. */}
          {top && top.zero_result_searches?.length ? (
            <div className="mt-4">
              <p className="mb-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-neutral-500">
                searches with no results
              </p>
              <ul className="divide-y divide-neutral-200" data-testid="panel-zero-searches">
                {top.zero_result_searches.map((s) => (
                  <li key={s.key} className="flex items-center justify-between py-2 text-sm">
                    <span className="truncate pr-2 lowercase">{s.key}</span>
                    <span className="font-mono text-xs text-neutral-600">{fmtNum(s.n)}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </Panel>
        <Panel title="taps + reactions" testId="panel-totals">
          {top && top.totals ? (
            <ul className="divide-y divide-neutral-200">
              {Object.entries(top.totals).map(([k, n]) => (
                <li key={k} className="flex items-center justify-between py-2 text-sm">
                  <span className="lowercase">{k.replace(/_/g, " ")}</span>
                  <span className="font-mono text-xs text-neutral-600">{fmtNum(n)}</span>
                </li>
              ))}
            </ul>
          ) : null}
        </Panel>
        <Panel title="referrers + UTM" testId="panel-referrers">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="mb-1 text-[11px] uppercase tracking-[0.14em] text-neutral-500">referrers</p>
              {refs.referrers.length ? (
                <ul className="divide-y divide-neutral-200">
                  {refs.referrers.slice(0, 10).map((r) => (
                    <li key={r.host} className="flex justify-between py-1 text-sm">
                      <span className="truncate pr-2 lowercase">{r.host}</span>
                      <span className="font-mono text-xs text-neutral-600">{r.n}</span>
                    </li>
                  ))}
                </ul>
              ) : <p className="text-xs text-neutral-400">no data</p>}
            </div>
            <div>
              <p className="mb-1 text-[11px] uppercase tracking-[0.14em] text-neutral-500">utm sources</p>
              {refs.utm_sources.length ? (
                <ul className="divide-y divide-neutral-200">
                  {refs.utm_sources.slice(0, 10).map((r) => (
                    <li key={r.source} className="flex justify-between py-1 text-sm">
                      <span className="truncate pr-2 lowercase">{r.source}</span>
                      <span className="font-mono text-xs text-neutral-600">{r.n}</span>
                    </li>
                  ))}
                </ul>
              ) : <p className="text-xs text-neutral-400">no data</p>}
            </div>
          </div>
        </Panel>
        <Panel title="devices" testId="panel-devices">
          {devices ? (
            <div className="grid grid-cols-3 gap-3 text-sm">
              {[["kind","device"],["os","os"],["app","app"]].map(([k, label]) => (
                <div key={k}>
                  <p className="mb-1 text-[11px] uppercase tracking-[0.14em] text-neutral-500">{label}</p>
                  <ul className="divide-y divide-neutral-200">
                    {Object.entries(devices[k] || {}).sort((a,b)=>b[1]-a[1]).map(([n,count]) => (
                      <li key={n} className="flex justify-between py-1">
                        <span className="lowercase">{n}</span>
                        <span className="font-mono text-xs text-neutral-600">{count}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          ) : null}
        </Panel>
        <Panel title="gender totals (hidden <5)" testId="panel-gender">
          {gender ? (
            <>
              {/* Wave 16b.4 (Vania 2 Oct) · surface the real denominator so
                  the % reading is honest. "active_members" = distinct users
                  with ≥1 event in the selected date range. */}
              {gender.active_members != null && (
                <p className="mb-2 text-[11px] uppercase tracking-[0.14em] text-neutral-500"
                   data-testid="gender-active-members">
                  active members in range: <span className="font-mono text-neutral-800">{fmtNum(gender.active_members)}</span>
                </p>
              )}
              <ul className="divide-y divide-neutral-200">
                {Object.entries(gender.counts).map(([k, v]) => (
                  <li key={k} className="flex items-center justify-between py-2 text-sm">
                    <span className="lowercase">{k.replace(/_/g, " ")}</span>
                    <span className="font-mono text-xs text-neutral-600">
                      {v == null ? <span className="italic text-neutral-400">fewer than 5</span> : fmtNum(v)}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          ) : null}
        </Panel>
        <Panel title="cities (ip geolocation, city granularity)" testId="panel-cities">
          {cities ? (
            <>
              <KenyaMap cities={cities.cities} />
              <p className="mt-3 mb-1 text-[11px] uppercase tracking-[0.14em] text-neutral-500">nairobi areas (member-chosen)</p>
              {cities.nairobi_areas?.length ? (
                <ul className="divide-y divide-neutral-200 max-h-56 overflow-auto">
                  {cities.nairobi_areas.map((a) => (
                    <li key={a.area} className="flex justify-between py-1 text-sm">
                      <span className="lowercase">{a.area}</span>
                      <span className="font-mono text-xs text-neutral-600">{a.n}</span>
                    </li>
                  ))}
                </ul>
              ) : <p className="text-xs text-neutral-400">no member picks yet</p>}
            </>
          ) : null}
        </Panel>
      </div>
    </div>
  );
}

function Panel({ title, children, testId }) {
  return (
    <section data-testid={testId} className="rounded-xl border border-neutral-200 bg-white p-4">
      <h3 className="mb-3 text-sm font-semibold uppercase tracking-[0.14em] text-neutral-500">{title}</h3>
      {children}
    </section>
  );
}

/** Minimal SVG line chart. Shows all series overlaid. No deps. */
function SparkChart({ series, loading }) {
  if (loading) {
    return <div data-testid="analytics-chart-loading" className="rounded-xl border border-neutral-200 bg-white p-6 text-sm text-neutral-400">loading…</div>;
  }
  if (!series || series.length === 0) {
    return <div className="rounded-xl border border-neutral-200 bg-white p-6 text-sm text-neutral-400">no activity in this range yet</div>;
  }
  const w = 900, h = 180, pad = 30;
  const metrics = ["pageview", "signin_success", "account_created", "trials_started"];
  const colours = { pageview: "#1A1A30", signin_success: "#5390FF", account_created: "#44DD88", trials_started: "#FF6B9D" };
  const maxY = Math.max(1, ...series.flatMap((d) => metrics.map((m) => d[m] || 0)));
  const xStep = series.length > 1 ? (w - pad * 2) / (series.length - 1) : 0;
  const toPath = (metric) => series.map((d, i) => {
    const x = pad + i * xStep;
    const y = h - pad - ((d[metric] || 0) / maxY) * (h - pad * 2);
    return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-3">
      <svg viewBox={`0 0 ${w} ${h}`} data-testid="analytics-chart" style={{ width: "100%", height: "auto" }}>
        <line x1={pad} y1={h - pad} x2={w - pad} y2={h - pad} stroke="#e5e5e5" />
        {metrics.map((m) => (
          <path key={m} d={toPath(m)} fill="none" stroke={colours[m]} strokeWidth="2" />
        ))}
        {series.map((d, i) => (
          <text key={d.date} x={pad + i * xStep} y={h - pad + 14}
                textAnchor="middle" fontSize="9" fill="#999">
            {d.date.slice(5)}
          </text>
        ))}
      </svg>
      <div className="mt-2 flex flex-wrap gap-4 text-xs">
        {metrics.map((m) => (
          <span key={m} className="inline-flex items-center gap-1">
            <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: 2, background: colours[m] }} />
            {m.replace(/_/g, " ")}
          </span>
        ))}
      </div>
    </div>
  );
}

/** Tiny Kenya outline + city dots. The outline is a rough bounding polygon;
 *  dots are plotted by lat/lon within that bbox. Not interactive. */
function KenyaMap({ cities }) {
  // Kenya rough bbox: 34°E–42°E, -5°S–5°N
  const minLon = 34, maxLon = 42, minLat = -5, maxLat = 5;
  const w = 360, h = 360;
  const toXY = (lat, lon) => {
    const x = ((lon - minLon) / (maxLon - minLon)) * w;
    const y = h - ((lat - minLat) / (maxLat - minLat)) * h;
    return [x, y];
  };
  // Rough simplified outline (not geopolitically accurate, visual guide only)
  const outline = "M 60,320 L 100,280 L 200,260 L 290,250 L 320,220 L 310,160 L 300,90 L 220,50 L 160,70 L 90,110 L 60,180 Z";
  return (
    <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-3">
      <svg viewBox={`0 0 ${w} ${h}`} style={{ width: "100%", maxWidth: 420 }} data-testid="analytics-map">
        <path d={outline} fill="#F1E7D6" stroke="#8A8171" strokeWidth="1" />
        {(cities || []).map((c, i) => {
          if (!c.lat || !c.lon) return null;
          if (c.lat < minLat || c.lat > maxLat || c.lon < minLon || c.lon > maxLon) return null;
          const [x, y] = toXY(c.lat, c.lon);
          const r = Math.max(3, Math.min(12, Math.sqrt(c.n) * 2));
          return (
            <g key={`${c.city}-${i}`}>
              <circle cx={x} cy={y} r={r} fill="#FF6B9D" opacity="0.6" />
              <title>{c.city || c.country}: {c.n}</title>
            </g>
          );
        })}
      </svg>
      <p className="text-[11px] text-neutral-500">
        showing {cities?.length || 0} cities (kenya + worldwide; map outline is kenya bbox)
      </p>
    </div>
  );
}
